import { format } from 'date-fns/format';
import { de } from 'date-fns/locale';
import { TFunction } from 'i18next';

export type DateFormatType =
  | 'digits'
  | 'longMonthFirst'
  | 'longMonthMiddle'
  | 'iso'
  | 'dayWordThenFullDate';

type FormatDateProps = {
  date: Date | string;
  formatType: DateFormatType;
};

const dateFormatMap: Record<DateFormatType, string> = {
  digits: 'dd.MM.yyyy',
  longMonthFirst: 'LLL dd, y',
  longMonthMiddle: 'dd. LLL. y',
  iso: 'yyyy-MM-dd',
  dayWordThenFullDate: 'EEEE, dd. LLL. y',
};

export const formatDate = ({ date, formatType }: FormatDateProps) =>
  format(date, dateFormatMap[formatType], {
    locale: de,
  });

export const formatCompactNumber = (value: number): string => {
  const absValue = Math.abs(value);
  const sign = value < 0 ? '-' : '';

  if (absValue >= 1_000_000_000) {
    return `${sign}${(absValue / 1_000_000_000).toFixed(0)}B`;
  }
  if (absValue >= 1_000_000) {
    return `${sign}${(absValue / 1_000_000).toFixed(0)}M`;
  }
  if (absValue >= 1_000) {
    return `${sign}${(absValue / 1_000).toFixed(0)}K`;
  }

  return new Intl.NumberFormat('de-DE', {
    maximumFractionDigits: 0,
  }).format(value);
};

export const formatNumberWithDots = (
  value: number | null | undefined,
  maximumFractionDigits = 0,
): string => {
  if (value === null || value === undefined || isNaN(value)) {
    return '';
  }
  return new Intl.NumberFormat('de-DE', {
    maximumFractionDigits,
  }).format(value);
};

/**
 * Formats numbers for dashboard cards with limited space.
 * Uses full dot-separated format for smaller numbers,
 * and compact abbreviations (K/M/B) for larger ones.
 */
export const formatDashboardNumber = (
  value: number | null | undefined,
): string => {
  if (value === null || value === undefined || isNaN(value)) {
    return '';
  }
  const absValue = Math.abs(value);
  const sign = value < 0 ? '-' : '';

  if (absValue >= 1_000_000_000) {
    const formatted = (absValue / 1_000_000_000).toFixed(1).replace('.', ',');
    const cleaned = formatted.replace(',0', '');
    return `${sign}${cleaned} Mrd.`;
  }
  if (absValue >= 1_000_000) {
    const formatted = (absValue / 1_000_000).toFixed(1).replace('.', ',');
    const cleaned = formatted.replace(',0', '');
    return `${sign}${cleaned} Mio.`;
  }
  if (absValue >= 10_000) {
    const formatted = (absValue / 1_000).toFixed(1).replace('.', ',');
    const cleaned = formatted.replace(',0', '');
    return `${sign}${cleaned} Tsd.`;
  }

  return formatNumberWithDots(value);
};

export const formatPercentage = (value: number): string => {
  const absValue = Math.abs(value);
  if (absValue >= 1_000) {
    return formatNumberWithDots(Math.round(absValue));
  }
  return absValue.toFixed(1).replace('.', ',');
};

export const parseFormattedNumber = (value: string): number => {
  const cleaned = value.replace(/\./g, '').replace(',', '.');
  const parsed = parseFloat(cleaned);
  return isNaN(parsed) ? 0 : parsed;
};

type FormatRelativeTimeProps = {
  date: Date | string;
  t: TFunction<'common'>;
};

export const formatRelativeTime = ({
  date,
  t,
}: FormatRelativeTimeProps): string => {
  const now = new Date();
  const pastDate = new Date(date);
  const diffMs = now.getTime() - pastDate.getTime();

  const MINUTE_MS = 60 * 1000;
  const HOUR_MS = 60 * MINUTE_MS;
  const DAY_MS = 24 * HOUR_MS;
  const WEEK_MS = 7 * DAY_MS;
  const MONTH_MS = 30 * DAY_MS;
  const YEAR_MS = 365 * DAY_MS;

  // Less than 1 hour: show minutes
  if (diffMs < HOUR_MS) {
    const minutes = Math.floor(diffMs / MINUTE_MS);
    return t('timestamp.minutes_ago', { count: minutes });
  }

  // Less than 24 hours: show hours
  if (diffMs < DAY_MS) {
    const hours = Math.floor(diffMs / HOUR_MS);
    return t('timestamp.hours_ago', { count: hours });
  }

  // Less than 7 days: show days
  if (diffMs < 7 * DAY_MS) {
    const days = Math.floor(diffMs / DAY_MS);
    return t('timestamp.days_ago', { count: days });
  }

  // Less than 28 days (4 weeks): show weeks
  if (diffMs < 28 * DAY_MS) {
    const weeks = Math.floor(diffMs / WEEK_MS);
    return t('timestamp.weeks_ago', { count: weeks });
  }

  // Less than 52 weeks: show months
  if (diffMs < 52 * WEEK_MS) {
    const months = Math.floor(diffMs / MONTH_MS);
    return t('timestamp.months_ago', { count: months });
  }

  // Else: show years
  const years = Math.floor(diffMs / YEAR_MS);
  return t('timestamp.years_ago', { count: years });
};
